import { useCallback } from 'react';
import { useMutation, useQueryClient } from '@common-fe/common-fe';
import { AxiosResponse } from 'axios';
import { toString } from 'lodash';

import { api } from '@/api';
import { API_URL, ILetterOfMedicalNecessityDto, ILetterOfMedicalNecessityPatchDto } from '@/dtos';

import { LettersOfMedicalNecessity } from '../LettersOfMedicalNecessity.types';

interface Payload {
  id: string;
  description?: string;
  name?: string;
  validFrom?: string | null;
  validTo?: string | null;
}

const useUpdateLmnItemQuery = () => {
  const queryClient = useQueryClient();

  const { mutateAsync, isLoading } = useMutation(
    (payload: Payload) =>
      api.patch<ILetterOfMedicalNecessityPatchDto, AxiosResponse<ILetterOfMedicalNecessityDto>>(
        API_URL.LETTERS_OF_MEDICAL_NECESSITY_BY_ID(payload.id),
        {
          description: payload.description || undefined,
          name: payload.name,
          valid_from: payload.validFrom,
          valid_to: payload.validTo,
        }
      ),
    {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [API_URL.LETTERS_OF_MEDICAL_NECESSITY],
        });
      },
    }
  );

  const updateLMN = useCallback(
    async (payload: Payload): Promise<LettersOfMedicalNecessity> => {
      const updatedLMN = await mutateAsync(payload);
      return {
        id: toString(updatedLMN?.data?.id),
        name: updatedLMN?.data?.name,
        description: updatedLMN?.data?.description,
        validFrom: updatedLMN?.data?.valid_to as unknown as string,
        validTo: updatedLMN?.data?.valid_to as unknown as string,
        // TODO: this field is absent
        // @ts-ignore
        fileUrl: updatedLMN?.data?.file_url,
        documentId: toString(updatedLMN?.data?.document_id),
        employeeId: toString(updatedLMN?.data?.employee_id),
        organizationId: toString(updatedLMN?.data?.organization_id),
        organizationPath: updatedLMN?.data?.organization_path,
        // TODO: this field is absent
        // @ts-ignore
        providerId: toString(updatedLMN?.data?.provider_id),
        createdAt: updatedLMN?.data?.created_at as unknown as string,
      };
    },
    [mutateAsync]
  );

  return {
    updateLMN,
    isLoading,
  };
};

export default useUpdateLmnItemQuery;
